// The Voices card of the identity page (ADR-0077): the person's voiceprints, the voice
// sample(s) first, then the recording voices confirmed to them. Pure — the reads and the
// writes live in `useIdentityVoices`, the player is handed in per row, so the card can be
// told a story with mock data. Divided rows rather than tiles: a voice has no picture, and
// what tells rows apart is heard, not seen.
import { AudioLines, Mic, Network } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { FaceActionNotice } from '@/components/faces/FaceActionNotice'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import type { DecisionErrorKey } from '@/features/face-matching/errors'
import type { IdentityVoiceprint } from '@/services/api/identity-voices'
import { VoiceQualityLine, VoiceWhen } from './voice-facts'

const COPY = 'identity.voices'

/** A voice sample on its way: recorded or added, but holding no voiceprint yet, or none at all. */
export interface PendingSampleRow {
  capture_id: string
  recorded_at: string
  /** The capture's state, as the audio tab reads it. */
  state: string
}

export interface IdentityVoicesCardState {
  /** The voiceprint whose request is in flight. */
  pendingId: string | null
  isLoading: boolean
  /** The last change's refusal, as the suffix of its i18n key; null when it went through. */
  errorKey: DecisionErrorKey | null
}

/** A missing callback makes that part read-only (a viewer, PRD-0032 SC-036). */
export interface IdentityVoicesCardActions {
  onToggleReference?: (voiceprintId: string, reference: boolean) => void
  onShowInGraph: (voiceprintId: string) => void
}

export interface IdentityVoicesCardProps {
  voiceprints: IdentityVoiceprint[]
  pendingSamples?: PendingSampleRow[]
  state: IdentityVoicesCardState
  actions: IdentityVoicesCardActions
  /** The voiceprint's player, connected by the host; a line saying there is no clip when it cannot play. */
  player: (voiceprint: IdentityVoiceprint) => ReactNode
}

export function IdentityVoicesCard({
  voiceprints,
  pendingSamples = [],
  state,
  actions,
  player,
}: IdentityVoicesCardProps) {
  const { t } = useTranslation()
  const readOnly = voiceprints.length > 0 && !actions.onToggleReference
  const empty = !state.isLoading && voiceprints.length === 0 && pendingSamples.length === 0
  return (
    <Card className="bg-card border-border">
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <CardTitle className="flex flex-wrap items-center gap-2">
          {t(`${COPY}.title`)}
          <Badge variant="muted">{t(`${COPY}.count`, { count: voiceprints.length })}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <FaceActionNotice
          copy={`${COPY}.errors`}
          errorKey={state.errorKey}
          readOnly={readOnly}
          readOnlyKey={`${COPY}.readOnly`}
        />
        {state.isLoading ? (
          <p className="text-sm text-muted-foreground">{t(`${COPY}.loading`)}</p>
        ) : null}
        {empty ? <p className="text-sm text-muted-foreground">{t(`${COPY}.empty`)}</p> : null}
        <ul className="divide-y divide-border">
          {voiceprints.map(voiceprint => (
            <IdentityVoiceItem
              key={voiceprint.voiceprint_id}
              voiceprint={voiceprint}
              state={state}
              actions={actions}
              player={player(voiceprint)}
            />
          ))}
          {pendingSamples.map(sample => (
            <PendingSampleItem key={sample.capture_id} sample={sample} />
          ))}
        </ul>
      </CardContent>
    </Card>
  )
}

function VoiceTitle({ voiceprint }: { voiceprint: IdentityVoiceprint }) {
  const { t } = useTranslation()
  const sample = voiceprint.origin === 'sample'
  return (
    <h3 className="flex flex-wrap items-center gap-2 font-medium">
      {sample ? t(`${COPY}.sample`) : t(`${COPY}.recordingVoice`, { voice: voiceprint.voice })}
      {voiceprint.reference ? <Badge variant="success">{t(`${COPY}.reference`)}</Badge> : null}
    </h3>
  )
}

function ReferenceSwitch({
  voiceprint,
  state,
  onToggle,
}: {
  voiceprint: IdentityVoiceprint
  state: IdentityVoicesCardState
  onToggle?: (voiceprintId: string, reference: boolean) => void
}) {
  const { t } = useTranslation()
  const pending = state.pendingId === voiceprint.voiceprint_id
  // A voiceprint that cannot be a reference keeps its switch off and says why; a switched-on
  // one is always offered the way off.
  const blocked = !voiceprint.reference && voiceprint.reference_blocker !== null
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <label className="flex min-w-0 items-center gap-2 text-2xs text-muted-foreground max-md:min-h-9">
        <Switch
          size="sm"
          checked={voiceprint.reference}
          disabled={pending || blocked || !onToggle}
          aria-label={t(`${COPY}.actions.useAsReference`)}
          data-voiceprint-id={voiceprint.voiceprint_id}
          onCheckedChange={next => onToggle?.(voiceprint.voiceprint_id, next)}
        />
        {t(`${COPY}.actions.useAsReference`)}
      </label>
      {blocked && voiceprint.reference_blocker ? (
        <p className="text-xs text-muted-foreground">
          {t(`${COPY}.blocked.${voiceprint.reference_blocker}`, { minimum: 10 })}
        </p>
      ) : null}
    </div>
  )
}

function IdentityVoiceItem({
  voiceprint,
  state,
  actions,
  player,
}: {
  voiceprint: IdentityVoiceprint
  state: IdentityVoicesCardState
  actions: IdentityVoicesCardActions
  player: ReactNode
}) {
  const { t } = useTranslation()
  const Icon = voiceprint.origin === 'sample' ? Mic : AudioLines
  const pending = state.pendingId === voiceprint.voiceprint_id
  return (
    <li
      data-voiceprint-id={voiceprint.voiceprint_id}
      className="grid gap-4 py-5 first:pt-0 last:pb-0 sm:grid-cols-[minmax(0,1fr)_minmax(14rem,22rem)]"
    >
      <div className="flex min-w-0 gap-3">
        <Icon aria-hidden className="mt-1 size-5 shrink-0 text-muted-foreground" />
        <div className="min-w-0 space-y-1.5">
          <VoiceTitle voiceprint={voiceprint} />
          {voiceprint.document_name ? (
            <p className="truncate text-sm text-muted-foreground" title={voiceprint.document_name}>
              {voiceprint.document_name}
            </p>
          ) : null}
          <VoiceWhen iso={voiceprint.recorded_at} />
          <VoiceQualityLine voiceprint={voiceprint} />
        </div>
      </div>
      <div className="flex flex-col gap-3 sm:items-end">
        <div className="w-full sm:flex sm:justify-end">{player}</div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 sm:justify-end">
          <ReferenceSwitch
            voiceprint={voiceprint}
            state={state}
            onToggle={actions.onToggleReference}
          />
          <Button
            variant="outline"
            size="sm"
            disabled={pending}
            onClick={() => actions.onShowInGraph(voiceprint.voiceprint_id)}
          >
            <Network className="size-3.5" />
            {t(`${COPY}.actions.showInGraph`)}
          </Button>
        </div>
      </div>
    </li>
  )
}

/** A sample the worker is still checking, or one that gave no voiceprint: its state in words. */
function PendingSampleItem({ sample }: { sample: PendingSampleRow }) {
  const { t } = useTranslation()
  return (
    <li className="flex gap-3 py-5 first:pt-0 last:pb-0">
      <Mic aria-hidden className="mt-1 size-5 shrink-0 text-muted-foreground" />
      <div className="min-w-0 space-y-1.5">
        <h3 className="font-medium">{t(`${COPY}.sample`)}</h3>
        <VoiceWhen iso={sample.recorded_at} />
        <p className="text-sm text-muted-foreground">
          {t(`identity.detail.audio.states.${sample.state}`, { defaultValue: sample.state })}
        </p>
      </div>
    </li>
  )
}
